import { setAuth } from "store/auth.store";
import type { IBodyAuthUser } from "types/auth.types";
import type { IUser } from "types/user.types";
import { api } from "utils/axios";
import { identifyPostHogUser, postHogFirstTouchFields } from "utils/posthog";

// The browser's PostHog first touch is attached HERE, not at the four call
// sites, because this is the one function every `/auth/user` request goes
// through — and any of them can be the request that creates the account whose
// trial `trial_started` then has to attribute (a brand-new Google account
// signing in from /signin takes the register path too). The API ignores it for
// an existing user and for an AppSumo purchase.
//
// It is in the body because the `Cookie` header the API would rather read does
// not survive the cross-origin hop to api.equilityhq.com — see
// postHogFirstTouchFields (EQU-481). Empty when PostHog is blocked, which the
// API reads as "no landing page" rather than a guess.
// 🪤 First touch spreads FIRST so the real body always wins a key collision.
// Instrumentation must never be able to rewrite the request it rides on — the
// Firebase token in particular.
const postAuthUser = (body: IBodyAuthUser) =>
	api
		.post<any>("/auth/user", { ...postHogFirstTouchFields(), ...body })
		.then((res) => res.data); // returns the User entity, not IAuthCredentials

export function useAuthUser() {
	// NOTE: intentionally NOT a keyed useSWR. `/auth/user` is a POST mutation that
	// verifies the Firebase ID token carried in the request *body*. A keyed
	// `useSWR(["/auth/user", null], fetcher)` made SWR auto-fire the fetcher on
	// mount as `fetcher("/auth/user", null)` — a tokenless POST that reached the
	// backend with `token: undefined` and surfaced the raw
	// "First argument to verifyIdToken() must be a Firebase ID token string."
	// error. It must only be called explicitly, with a real token, via authUser().
	const authUser = async (body: IBodyAuthUser) => {
		try {
			const result = await postAuthUser(body);

			// After successful authentication, update auth store
			if (result) {
				await setAuth(result.firebaseUid); // Use firebaseUid from User entity

				// Identify here, at the account-creation / sign-in boundary, rather
				// than only from `useUser`.
				//
				// 🪤 `useUser` lives under <Protected>, so its identify fires only
				// once the app shell mounts — which a brand-new sign-up does NOT
				// reach: it is sent to /verify-email, a route outside Protected that
				// never fetches `user`. Those accounts were created and then never
				// identified, so their pre-signup pageviews stayed anonymous and the
				// signup step of the funnel undercounted. Over the 60 days to
				// 2026-10-03, 8 of the 27 signup-page sessions that got past the form
				// showed in-app or /verify-email activity with no `$identify` at all.
				//
				// `/auth/user` answers with the User entity, so the stable numeric id
				// is already in hand at exactly the moment the account exists.
				// `useUser` still calls this later; a repeat identify to the same
				// distinct id is a no-op.
				identifyPostHogUser(result as IUser);
			}

			return result;
		} catch (error: any) {
			// A coded refusal nests its text: { message: { code, message } }.
			const data = error.response?.data?.message;
			const message =
				(typeof data === "string" ? data : data?.message) || error.message;
			throw new Error(message || "Auth failed");
		}
	};

	return { authUser };
}
